/**
 * Button Component
 *  */

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { LoadingSpinner } from '../feedback/LoadingSpinner';

const buttonVariants = cva(
  [
    // gap-2 spaces icon and text; [&_svg]:mr-0 cancels the extra margin older icons carry.
    'group relative inline-flex shrink-0 items-center justify-center gap-2 overflow-hidden whitespace-nowrap [&_svg]:mr-0',
    'rounded-md text-sm font-semibold tracking-tight outline-none',
    'transition-all duration-200 ease-out',
    'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-55',
    'aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed aria-disabled:opacity-55',
    'data-[loading=true]:cursor-wait',
    'motion-safe:hover:-translate-y-0.5 motion-safe:active:translate-y-0',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-sm shadow-primary/25 hover:bg-primary/90 hover:shadow-md hover:shadow-primary/30 active:shadow-sm',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm shadow-destructive/20 hover:bg-destructive/90 hover:shadow-md hover:shadow-destructive/25',
        outline:
          'border border-input bg-background/80 text-foreground shadow-sm hover:border-primary/30 hover:bg-accent hover:text-accent-foreground hover:shadow-md',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 hover:shadow-md',
        ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground hover:shadow-sm',
        link: 'h-auto rounded-sm px-0 py-0 text-primary underline-offset-4 shadow-none hover:translate-y-0 hover:text-primary/85 hover:underline focus-visible:ring-offset-0',
        success: 'bg-success text-success-foreground shadow-sm shadow-success/20 hover:bg-success/90 hover:shadow-md hover:shadow-success/25',
        soft: 'bg-primary/10 text-primary shadow-none hover:bg-primary/15 hover:shadow-sm',
        premium:
          'bg-gradient-to-r from-primary via-primary to-primary/80 text-primary-foreground shadow-sm shadow-primary/30 hover:brightness-105 hover:shadow-lg hover:shadow-primary/35',
      },
      size: {
        xs: 'h-8 rounded-md px-2.5 text-xs [&_svg]:h-3.5 [&_svg]:w-3.5',
        sm: 'h-9 rounded-md px-3 text-xs [&_svg]:h-4 [&_svg]:w-4',
        default: 'h-10 px-4 py-2 [&_svg]:h-4 [&_svg]:w-4',
        lg: 'h-11 rounded-lg px-6 text-base [&_svg]:h-5 [&_svg]:w-5',
        icon: 'h-10 w-10 p-0 [&_svg]:h-4 [&_svg]:w-4',
        'icon-sm': 'h-8 w-8 p-0 [&_svg]:h-3.5 [&_svg]:w-3.5',
        'icon-lg': 'h-11 w-11 rounded-lg p-0 [&_svg]:h-5 [&_svg]:w-5',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const isDisabled = disabled || loading;
    const usesPrimarySpinner = variant === 'outline' || variant === 'secondary' || variant === 'ghost' || variant === 'soft';
    const spinnerTone = usesPrimarySpinner ? 'border-primary/20 border-t-primary' : 'border-primary-foreground/30 border-t-primary-foreground';

    // When asChild is true, Slot expects exactly one child
    // So we cannot add LoadingSpinner alongside children
    if (asChild) {
      return (
        <Slot
          aria-disabled={isDisabled || undefined}
          className={cn(buttonVariants({ variant, size, fullWidth, className }))}
          data-loading={loading ? 'true' : undefined}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        data-loading={loading ? 'true' : undefined}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading && <LoadingSpinner size="sm" className={spinnerTone} />}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
